import { JSONContent } from '@tiptap/react';
import { DocumentContent, DocumentSourceType, RichTextDocumentContent } from '../types/document';

export const isOrdinaryDocument = (sourceType?: DocumentSourceType, content?: DocumentContent): boolean =>
  sourceType !== 'questionnaire' && content?.kind !== 'questionnaire' &&
  Boolean(sourceType || (content && ['will', 'pdf', 'rich_text', 'legacy'].includes(content.kind)));

export const textToEditorDocument = (text: string): JSONContent => ({
  type: 'doc',
  content: text.split(/\n\n+/).map(paragraph => ({
    type: 'paragraph',
    content: paragraph.split('\n').flatMap((line, index) => [
      ...(index > 0 ? [{ type: 'hardBreak' }] : []),
      ...(line ? [{ type: 'text', text: line }] : []),
    ]),
  })),
});

export const editorDocumentToText = (node: JSONContent): string => {
  if (node.type === 'text') return node.text || '';
  if (node.type === 'hardBreak') return '\n';
  const children = (node.content || []).map(editorDocumentToText);
  if (node.type === 'tableRow') return children.join(' | ');
  if (['paragraph', 'heading', 'codeBlock'].includes(node.type || '')) return children.join('');
  return children.join('\n\n');
};

export const createRichTextContent = (editorContent: JSONContent): RichTextDocumentContent => ({
  kind: 'rich_text',
  editorSchemaVersion: 1,
  editorContent,
  bodyText: editorDocumentToText(editorContent),
});

export const toRichTextContent = (content?: DocumentContent, fallbackText = ''): RichTextDocumentContent => {
  if (content?.kind === 'rich_text') {
    if (content.editorContent && typeof content.editorContent === 'object') return content;
    return createRichTextContent(textToEditorDocument(
      typeof content.editorContent === 'string' ? content.editorContent : content.bodyText || fallbackText,
    ));
  }
  if (content?.kind === 'pdf') {
    const nodes: JSONContent[] = content.blocks.map(block => {
      if (block.type === 'table') {
        const cell = (text: string, type: string): JSONContent => ({
          type,
          content: [{ type: 'paragraph', content: text ? [{ type: 'text', text }] : [] }],
        });
        const headers = block.tableData?.headers || [];
        return {
          type: 'table',
          content: [
            ...(headers.length ? [{ type: 'tableRow', content: headers.map(text => cell(text, 'tableHeader')) }] : []),
            ...(block.tableData?.rows || []).map(row => ({
              type: 'tableRow', content: row.map(text => cell(text, 'tableCell')),
            })),
          ],
        };
      }
      return {
        type: block.type === 'heading' ? 'heading' : 'paragraph',
        ...(block.type === 'heading' ? { attrs: { level: Math.min(Math.max(block.level || 1, 1), 6) } } : {}),
        content: textToEditorDocument(block.text || '').content?.flatMap((paragraph, index) => [
          ...(index > 0 ? [{ type: 'hardBreak' }, { type: 'hardBreak' }] : []),
          ...(paragraph.content || []),
        ]),
      };
    });
    return createRichTextContent({ type: 'doc', content: nodes });
  }
  const text = content?.kind === 'will' ? content.bodyText : fallbackText ||
    (content?.kind === 'legacy' ? content.templateText : '');
  return createRichTextContent(textToEditorDocument(text));
};
